// A venue featured in several guides comes back from the API as one feature
// per guide. Merge those into a single feature that lists every guide and
// every category. Records are the same venue when they share coordinates,
// a street address plus the first word of the name ("Village Well" and
// "Village Well Books" at 9900 Culver Blvd), or a name within 150 m, since
// older records for one venue were often geocoded a few meters apart. The
// first-word check keeps separate venues in one mall or food hall apart.

// Some older records were saved with HTML entities ("Bloom &amp; Plume"),
// which would otherwise show up literally in the list and popups
const decodeEntities = (text) =>
  typeof text === "string" && text.includes("&")
    ? new DOMParser().parseFromString(text, "text/html").documentElement
        .textContent
    : text;

const normalize = (text = "") =>
  decodeEntities(text)
    .toLowerCase()
    .replace(/^the\s+/, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// The street part of the address, only when it has a street number
const streetKey = (address = "") => {
  const street = normalize(address.split(",")[0]);
  return /\d/.test(street) ? street : null;
};

const metersBetween = ([lon1, lat1], [lon2, lat2]) => {
  const x = (lon2 - lon1) * Math.cos(((lat1 + lat2) / 2) * (Math.PI / 180));
  return Math.hypot(x, lat2 - lat1) * 111320;
};

export default function dedupeLocations(features) {
  const venues = [];
  const byCoords = new Map();
  const byStreet = new Map();
  const byName = new Map();

  // Records with a status (closed venues, towns and events that aren't
  // venues) stay in the database for history but don't go on the map
  const visible = features.filter((feature) => !feature.properties.status);

  visible.forEach((feature) => {
    const coords = feature.geometry.coordinates;
    const coordKey = coords.map((c) => c.toFixed(6)).join(",");
    const street = streetKey(feature.properties.Address);
    const name = normalize(feature.properties.Locations);
    const firstWord = name.split(" ")[0];
    const { Name, slug, category } = feature.properties;

    let venue =
      byCoords.get(coordKey) ||
      (street &&
        (byStreet.get(street) || []).find(
          (v) => normalize(v.properties.Locations).split(" ")[0] === firstWord
        )) ||
      (byName.get(name) || []).find(
        (v) => metersBetween(v.geometry.coordinates, coords) < 150
      );

    if (!venue) {
      venue = {
        type: "Feature",
        geometry: feature.geometry,
        properties: {
          ...feature.properties,
          Locations: decodeEntities(feature.properties.Locations),
          Address: decodeEntities(feature.properties.Address),
          id: coordKey,
          categories: [],
          guides: [],
        },
      };
      venues.push(venue);
    }

    byCoords.set(coordKey, venue);
    if (street) byStreet.set(street, [...(byStreet.get(street) || []), venue]);
    byName.set(name, [...(byName.get(name) || []), venue]);

    const merged = venue.properties;
    if (category && !merged.categories.includes(category)) {
      merged.categories.push(category);
    }
    if (slug && !merged.guides.some((guide) => guide.slug === slug)) {
      merged.guides.push({ Name: decodeEntities(Name), slug });
    }
  });

  return venues;
}
